/**
 * Advanced / debug switches: the viewer and Player URL parameters that make sense as a switch,
 * saved per device in one object (`walk-player.advanced`) so nobody has to type a URL on a tablet.
 *
 * An override behaves like a URL parameter that is always there: `mergeSearch` overlays the saved
 * overrides under the page's real query string, so a real `?param=` still wins for that page.
 * The Player reads the merged string where it used to read `location.search`, and
 * `applyToViewerQuery` puts the same values on the viewer iframe's URL. Pure apart from the
 * storage helpers (the storage is a parameter), so it runs under node:test.
 */

export const ADVANCED_KEY = "walk-player.advanced";

export type OverrideGroup = "Viewer" | "Texture formats" | "Quality" | "Reflections and materials" | "Walking" | "Memory";

export interface OverrideDef {
  /** The URL parameter, shown in small text so it can be quoted. */
  param: string;
  label: string;
  group: OverrideGroup;
  /** First option is always "" (not set: the Player's own choice). */
  options: { value: string; label: string }[];
  hint?: string;
}

const DEFAULT = { value: "", label: "default" };
const OFF = (label = "off"): { value: string; label: string } => ({ value: "0", label });
const ON = (label = "on"): { value: string; label: string } => ({ value: "1", label });

export const OVERRIDES: OverrideDef[] = [
  { param: "debug", label: "Viewer debug panel", group: "Viewer", options: [DEFAULT, ON("open at start")], hint: "also key I, or a three-finger tap" },
  { param: "lm_format", label: "Lightmap format", group: "Texture formats", options: [{ value: "", label: "auto (ASTC if the device can)" }, { value: "astc", label: "astc" }, { value: "rgb9e5", label: "rgb9e5" }, { value: "half", label: "half float" }] },
  { param: "tex_format", label: "Material texture format", group: "Texture formats", options: [{ value: "", label: "auto (ASTC if the walk has it)" }, { value: "orig", label: "orig (never ASTC)" }] },
  { param: "msaa", label: "Canvas MSAA", group: "Texture formats", options: [{ value: "", label: "automatic" }, ON("on (as before)"), OFF("off everywhere")] },
  { param: "release", label: "CPU copies of textures", group: "Texture formats", options: [{ value: "", label: "released after upload" }, { value: "0", label: "keep them" }] },
  { param: "bloom_half", label: "Glow at half resolution", group: "Quality", options: [{ value: "", label: "automatic (phone preset / over the memory limit)" }, ON(), OFF()] },
  { param: "blur_half", label: "Motion blur at half resolution", group: "Quality", options: [{ value: "", label: "automatic" }, ON(), OFF()] },
  { param: "max_dpr", label: "Most device pixels per CSS pixel", group: "Quality", options: [{ value: "", label: "automatic (2, phones 1.5)" }, { value: "0.5", label: "0.5" }, { value: "1", label: "1" }, { value: "1.5", label: "1.5" }, { value: "2", label: "2" }] },
  { param: "env", label: "Reflections", group: "Reflections and materials", options: [DEFAULT, OFF()] },
  { param: "so", label: "Reflection occlusion", group: "Reflections and materials", options: [DEFAULT, OFF()] },
  { param: "box", label: "Box-projected reflections", group: "Reflections and materials", options: [DEFAULT, OFF()] },
  { param: "lights", label: "Scene light highlights", group: "Reflections and materials", options: [DEFAULT, OFF()] },
  { param: "metalFloor", label: "Metal floor", group: "Reflections and materials", options: [{ value: "", label: "off (default)" }, { value: "0.35", label: "on (0.35)" }] },
  { param: "glassMinOpacity", label: "Least opacity of textured glass", group: "Reflections and materials", options: [DEFAULT, { value: "0", label: "0" }, { value: "1", label: "1" }] },
  { param: "slide", label: "Slide along edges (degrees)", group: "Walking", options: [{ value: "", label: "75" }, { value: "0", label: "0 (off)" }, { value: "30", label: "30" }, { value: "45", label: "45" }, { value: "60", label: "60" }] },
  { param: "gpu_mb", label: "Graphics memory estimate (MB)", group: "Memory", options: [{ value: "", label: "the walk's own" }, { value: "400", label: "400" }, { value: "800", label: "800 (over the iPad limit)" }, { value: "1600", label: "1600 (over the other limit)" }, { value: "3000", label: "3000" }], hint: "to test the memory warning; applies to the next walk you open" },
];

const BY_PARAM = new Map(OVERRIDES.map((def) => [def.param, def]));

export type Overrides = Record<string, string>;

/** Settings that already have their own controls: listed in the menu, not duplicated. */
export const ELSEWHERE: { params: string; where: string }[] = [
  { params: "kiosk, fps", where: "switches in this menu and in the gear panel" },
  { params: "glow, outdoor, motion_blur", where: "the gear/sun panels and the motion blur button" },
  { params: "look_sens, look_zoom", where: "the sun panel (look sensitivity)" },
  { params: "ae, ae_strength, ae_range, ae_speed, ae_meter, exposure", where: "the sun panel (exposure)" },
  { params: "lm", where: "the viewer debug panel (Light slider)" },
];

/** Only known parameters with one of their listed values survive; anything else is dropped. */
export function sanitizeOverrides(raw: unknown): Overrides {
  const out: Overrides = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [param, value] of Object.entries(raw as Record<string, unknown>)) {
    const def = BY_PARAM.get(param);
    if (def && typeof value === "string" && value !== "" && def.options.some((option) => option.value === value)) out[param] = value;
  }
  return out;
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function loadOverrides(storage: Store | null = safeLocalStorage()): Overrides {
  try {
    const raw = storage?.getItem(ADVANCED_KEY);
    return raw ? sanitizeOverrides(JSON.parse(raw)) : {};
  } catch {
    return {};
  }
}

/** Saves the object; an empty one removes the key. Returns false when the storage refused. */
export function saveOverrides(overrides: Overrides, storage: Store | null = safeLocalStorage()): boolean {
  try {
    const clean = sanitizeOverrides(overrides);
    if (Object.keys(clean).length === 0) storage?.removeItem(ADVANCED_KEY);
    else storage?.setItem(ADVANCED_KEY, JSON.stringify(clean));
    return storage !== null;
  } catch {
    return false;
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** The page's query string with the saved overrides added for every parameter the URL does not set itself. */
export function mergeSearch(overrides: Overrides, search: string): string {
  const params = new URLSearchParams(search);
  for (const [param, value] of Object.entries(overrides)) {
    if (!params.has(param)) params.set(param, value);
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

/** What the Player reads where it used to read `location.search`: saved overrides under the real URL. */
export function effectiveSearch(): string {
  return mergeSearch(loadOverrides(), typeof location === "undefined" ? "" : location.search);
}

/**
 * The viewer iframe's query with every switch of the catalog that is set (saved or in the URL)
 * written over what the Player computed, so a switch also beats the Player's automatic choice.
 */
export function applyToViewerQuery(query: string, search: string): string {
  const params = new URLSearchParams(query);
  const from = new URLSearchParams(search);
  for (const def of OVERRIDES) {
    const value = from.get(def.param);
    if (value !== null && value !== "") params.set(def.param, value);
  }
  return params.toString();
}

/** `gpu_mb` of the merged search as a positive number, for the memory warning. */
export function gpuMbOverride(search: string): number | undefined {
  const value = Number(new URLSearchParams(search).get("gpu_mb"));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export interface ActiveOverride {
  param: string;
  value: string;
  source: "saved" | "URL";
}

/** Every switch in force right now: saved ones, and URL ones (which win over a saved value). */
export function activeOverrides(overrides: Overrides, search: string): ActiveOverride[] {
  const url = new URLSearchParams(search);
  const rows: ActiveOverride[] = [];
  for (const def of OVERRIDES) {
    const fromUrl = url.get(def.param);
    if (fromUrl !== null && fromUrl !== "") rows.push({ param: def.param, value: fromUrl, source: "URL" });
    else if (overrides[def.param]) rows.push({ param: def.param, value: overrides[def.param] as string, source: "saved" });
  }
  return rows;
}

/** The page's own address with the active parameters: paste it on another device to get the same setup. */
export function urlWithActive(origin: string, pathname: string, overrides: Overrides, search: string): string {
  return `${origin}${pathname}${mergeSearch(overrides, search)}`;
}

/** Whether applying `next` instead of `previous` needs the viewer iframe reloaded, and whether the debug panel alone changed. */
export function changeKind(previous: Overrides, next: Overrides): "none" | "debug-only" | "reload" {
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  const changed = [...keys].filter((key) => previous[key] !== next[key]);
  if (changed.length === 0) return "none";
  return changed.every((key) => key === "debug") ? "debug-only" : "reload";
}
